import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DropdownMenuItem,
    Separator,
    Spinner,
} from "frontend-components";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faPencil, faPrint, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { subject } from "@casl/ability";

import { taskListWithTasksOptions } from "@dashboard/api/queryApi";
import type { TaskListWithTaskOccurrences } from "@dashboard/api/task";
import { Can } from "@dashboard/casl/taskAbility";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import { ConfirmDialog } from "@dashboard/components/common/ConfirmDialog";
import { ConfirmDiscardDialog } from "@dashboard/components/common/ConfirmDiscardDialog";
import { useCopyList, useDeleteList } from "@dashboard/hooks/useListQuery";
import { TemplateBadge } from "@dashboard/features/Dashboard/Lists/TemplateBadge";
import { exportChecklistPdf } from "@dashboard/features/Dashboard/Lists/exportChecklistPdf";

import { ListViewBody } from "./ListViewBody";
import { ListFormBody, type ListFormBodyHandle } from "./ListFormBody";

interface ListModalLoadedViewProps {
    mode: "view" | "edit";
    listId: number;
    onClose: () => void;
}

export function ListModalLoadedView({ mode, listId, onClose }: ListModalLoadedViewProps) {
    const { data: list, isLoading, error } = useQuery(taskListWithTasksOptions(listId));

    if (isLoading || error || !list || !list.id) {
        return (
            <ListModalStatusView
                isLoading={isLoading}
                errorMessage={error?.message}
                listId={listId}
                onClose={onClose}
            />
        );
    }

    return <ListModalLoadedContent mode={mode} list={list} onClose={onClose} />;
}

export function ListModalStatusView({
    isLoading,
    errorMessage,
    listId,
    onClose,
}: {
    isLoading: boolean;
    errorMessage?: string;
    listId: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent className="bg-compendia-card max-h-[90vh] overflow-y-auto overflow-x-hidden md:overflow-hidden flex flex-col sm:max-w-[calc(100%-2rem)] md:w-[calc(100vw-4rem)] md:max-w-[1300px] text-compendia-foreground gap-0">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                        {t("common.checklist")}
                    </DialogTitle>
                    <Separator />
                </DialogHeader>

                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <Spinner className="size-6" />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 py-6">
                        <h2 className="text-h3 font-medium">{t("common.listNotFound")}</h2>
                        <p className="text-text-secondary">
                            {errorMessage || `${t("common.couldNotFindListWithId")}: ${listId}`}
                        </p>
                        <div className="flex justify-end">
                            <Button variant="outline" onClick={onClose}>
                                {t("common.close")}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ListModalLoadedContent({
    mode,
    list,
    onClose,
}: {
    mode: "view" | "edit";
    list: TaskListWithTaskOccurrences;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const formRef = useRef<ListFormBodyHandle | null>(null);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const deleteMutation = useDeleteList();
    const copyMutation = useCopyList();

    const listSubject = subject("TaskList", {
        instanceId: list.instanceId,
        createdById: list.createdById,
    });

    const isFormDirty = () => mode === "edit" && (formRef.current?.isDirty() ?? false);

    const requestClose = () => {
        if (isFormDirty()) {
            setConfirmDiscardOpen(true);
            return;
        }
        onClose();
    };

    const goToView = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, list: list.id, editList: undefined }),
        });
    };

    const goToEdit = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, list: list.id, editList: 1 }),
        });
    };

    const handleCopy = async () => {
        const data = await copyMutation.mutateAsync(list);
        void navigate({
            to: ".",
            search: (prev) => ({
                ...prev,
                list: data.list.id,
                editList: undefined,
            }),
        });
    };

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync(list.id);
            onClose();
        } catch {
            // surfaced via mutation; keep modal open
        }
    };

    return (
        <>
            <Dialog
                open
                onOpenChange={(open) => {
                    if (!open) requestClose();
                }}
            >
                <DialogContent
                    className="bg-compendia-card max-h-[90vh] overflow-y-auto overflow-x-hidden md:overflow-hidden flex flex-col sm:max-w-[calc(100%-2rem)] md:w-[calc(100vw-4rem)] md:max-w-[1300px] text-compendia-foreground gap-0"
                    menuLabel={t("common.openMenu")}
                    menu={
                        mode === "view" ? (
                            <>
                                <Can I="update" this={listSubject}>
                                    <DropdownMenuItem onClick={goToEdit}>
                                        <FontAwesomeIcon icon={faPencil} />
                                        {t("common.edit")}
                                    </DropdownMenuItem>
                                </Can>
                                <Can I="create" this={listSubject}>
                                    <DropdownMenuItem
                                        disabled={copyMutation.isPending}
                                        onClick={() => setShowCopyDialog(true)}
                                    >
                                        <FontAwesomeIcon icon={faCopy} />
                                        {t("common.copy")}
                                    </DropdownMenuItem>
                                </Can>
                                <DropdownMenuItem onClick={() => exportChecklistPdf(list.id)}>
                                    <FontAwesomeIcon icon={faPrint} />
                                    {t("list.printOrSaveAsPdf")}
                                </DropdownMenuItem>
                                <Can I="delete" this={listSubject}>
                                    <DropdownMenuItem
                                        variant="destructive"
                                        onClick={() => setConfirmDeleteOpen(true)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                        {t("common.delete")}
                                    </DropdownMenuItem>
                                </Can>
                            </>
                        ) : undefined
                    }
                >
                    <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border flex-row items-center gap-3">
                        <DialogTitle
                            className={`text-lg font-medium text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap ${
                                mode === "edit" ? " tracking-wider" : ""
                            }`}
                        >
                            {mode === "edit" ? t("list.editListTitle") : list.title}
                        </DialogTitle>
                        {mode === "view" && list.template && <TemplateBadge />}
                    </DialogHeader>

                    {mode === "view" ? (
                        <ListViewBody list={list} />
                    ) : (
                        <ListFormBody
                            key={`edit-${list.id}`}
                            ref={formRef}
                            list={list}
                            onCancel={() => {
                                if (isFormDirty()) {
                                    setConfirmDiscardOpen(true);
                                    return;
                                }
                                goToView();
                            }}
                            onSaved={() => goToView()}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDiscardDialog
                open={confirmDiscardOpen}
                onOpenChange={setConfirmDiscardOpen}
                onConfirm={() => {
                    setConfirmDiscardOpen(false);
                    if (mode === "edit") {
                        goToView();
                    } else {
                        onClose();
                    }
                }}
            />

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={handleDelete}
                title={t("common.deleteList")}
                description={t("common.confirmDeleteList")}
            />

            <ConfirmDialog
                open={showCopyDialog}
                onOpenChange={setShowCopyDialog}
                onConfirm={handleCopy}
                title={t("common.copyList")}
                description={t("common.confirmCopyList")}
                confirmLabel={t("common.copy")}
            />
        </>
    );
}
